import { create } from 'zustand'
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { QRCode, CreateQRCodeInput } from '../models/QRCode'
import { useAuthStore } from './authStore'

// Generate random alphanumeric code
function generateCode(length: number = 8): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

interface QRCodeState {
  qrCodes: QRCode[]
  loading: boolean
  error: string | null
  fetchQRCodes: () => Promise<void>
  createQRCode: (input: CreateQRCodeInput) => Promise<string>
  deleteQRCode: (id: string) => Promise<void>
}

export const useQRCodeStore = create<QRCodeState>((set, get) => ({
  qrCodes: [],
  loading: false,
  error: null,

  fetchQRCodes: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(collection(db, 'qrCodes'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      const qrCodes: QRCode[] = snapshot.docs.map((doc) => {
        const data = doc.data()
        // Parse usedByUsers array
        const usedByUsers = (data.usedByUsers || []).map((usage: { userId: string; usedAt: string | Timestamp }) => ({
          userId: usage.userId,
          usedAt: typeof usage.usedAt === 'string'
            ? new Date(usage.usedAt)
            : (usage.usedAt as Timestamp).toDate(),
        }))

        return {
          id: doc.id,
          code: data.code,
          createdBy: data.createdBy,
          createdAt: (data.createdAt as Timestamp).toDate(),
          expiresAt: (data.expiresAt as Timestamp).toDate(),
          isUsed: data.isUsed ?? false,
          usedBy: data.usedBy,
          usedAt: data.usedAt
            ? (data.usedAt as Timestamp).toDate()
            : undefined,
          usedByUsers,
          maxUses: data.maxUses || 1,
          currentUses: data.currentUses || 0,
        }
      })

      set({ qrCodes, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入 QR Code 失敗',
      })
    }
  },

  createQRCode: async (input: CreateQRCodeInput) => {
    set({ loading: true, error: null })
    try {
      const admin = useAuthStore.getState().admin
      if (!admin) throw new Error('請先登入')

      const code = generateCode()
      const expiresAt = new Date()
      expiresAt.setHours(expiresAt.getHours() + input.expiresInHours)

      await addDoc(collection(db, 'qrCodes'), {
        code,
        createdBy: admin.email,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
        isUsed: false,
        maxUses: input.maxUses,
        currentUses: 0,
      })

      await get().fetchQRCodes()
      set({ loading: false })

      return code
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '建立 QR Code 失敗',
      })
      throw error
    }
  },

  deleteQRCode: async (id: string) => {
    set({ loading: true, error: null })
    try {
      await deleteDoc(doc(db, 'qrCodes', id))
      await get().fetchQRCodes()
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '刪除 QR Code 失敗',
      })
      throw error
    }
  },
}))
